import React, { useState, useEffect, useCallback } from 'react';
import { 
  TrendingUp, TrendingDown, Activity, CheckCircle, Clock, AlertTriangle,
  Users, Building2, Briefcase, FileText, HelpCircle, Award, Target,
  Calendar, ChevronDown, RefreshCw, BarChart3, Zap, UserPlus
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import EmptyState from './EmptyState';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';

const PERIOD_OPTIONS = [
  { value: 'all', label: 'All Time', shortLabel: 'All' },
  { value: 'ytd', label: 'Year to Date', shortLabel: 'YTD' },
  { value: 'month', label: 'This Month', shortLabel: 'Month' },
  { value: 'week', label: 'This Week', shortLabel: 'Week' },
  { value: 'today', label: 'Today', shortLabel: 'Today' },
  { value: 'custom', label: 'Custom Range', shortLabel: 'Custom' }
];

// Gradient-based Metric Card
function MetricCard({ 
  title, value, subtitle, icon: Icon, trend, trendValue, 
  color = 'slate', onClick, loading = false 
}) {
  const gradientMap = {
    slate:   'metric-gradient-slate',
    blue:    'metric-gradient-blue',
    emerald: 'metric-gradient-emerald',
    amber:   'metric-gradient-amber',
    red:     'metric-gradient-red',
    purple:  'metric-gradient-purple',
    pink:    'metric-gradient-rose',
    cyan:    'metric-gradient-cyan',
    indigo:  'metric-gradient-indigo',
    teal:    'metric-gradient-teal',
    violet:  'metric-gradient-violet',
    orange:  'metric-gradient-orange',
  };
  const gradientClass = gradientMap[color] || gradientMap.slate;

  if (loading) {
    return (
      <div className="rounded-2xl p-5 bg-white/60 backdrop-blur-sm border border-white/40">
        <Skeleton className="h-4 w-24 mb-3" />
        <Skeleton className="h-9 w-16 mb-1" />
        <Skeleton className="h-3 w-32" />
      </div>
    );
  }

  return (
    <div 
      className={`${gradientClass} rounded-2xl p-5 transition-all duration-200 ${onClick ? 'cursor-pointer hover:scale-[1.02] hover:shadow-lg' : ''}`}
      onClick={onClick}
      data-testid={`metric-card-${title?.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start justify-between mb-2">
        <p className="text-xs font-semibold text-white/80 tracking-wide">{title}</p>
        <div className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
          <Icon className="w-4 h-4 text-white" />
        </div>
      </div>
      <p className="text-3xl font-bold text-white font-mono-data">{value}</p>
      {subtitle && <p className="text-xs text-white/70 mt-1">{subtitle}</p>}
      {trend && trendValue !== undefined && (
        <div className="flex items-center gap-1 mt-2">
          {trend === 'up' ? <TrendingUp className="w-3 h-3 text-white/90" /> : <TrendingDown className="w-3 h-3 text-white/90" />}
          <span className="text-xs font-semibold text-white/90">{trendValue}</span>
        </div>
      )}
    </div>
  );
}

// Progress Metric Card with gradient
function ProgressMetricCard({ title, value, subtitle, icon: Icon, color = 'emerald', loading = false }) {
  const gradientMap = {
    emerald: 'metric-gradient-emerald',
    blue:    'metric-gradient-blue',
    amber:   'metric-gradient-amber',
    red:     'metric-gradient-red',
    violet:  'metric-gradient-violet',
    orange:  'metric-gradient-orange',
  };
  const gradientClass = gradientMap[color] || gradientMap.emerald;

  if (loading) {
    return (
      <div className="rounded-2xl p-5 bg-white/60 backdrop-blur-sm border border-white/40">
        <Skeleton className="h-4 w-24 mb-3" />
        <Skeleton className="h-9 w-16 mb-2" />
        <Skeleton className="h-2 w-full" />
      </div>
    );
  }

  return (
    <div className={`${gradientClass} rounded-2xl p-5`}
      data-testid={`progress-card-${title?.toLowerCase().replace(/\s+/g, '-')}`}>
      <p className="text-xs font-semibold text-white/80 tracking-wide mb-2">{title}</p>
      <p className="text-3xl font-bold text-white font-mono-data mb-2">{value}%</p>
      <div className="w-full bg-white/20 rounded-full h-2">
        <div className="h-2 rounded-full bg-white/70 transition-all" style={{ width: `${Math.min(value, 100)}%` }} />
      </div>
      {subtitle && <p className="text-xs text-white/70 mt-2">{subtitle}</p>}
    </div>
  );
}

// Department Breakdown Card
function DepartmentBreakdownCard({ departments, loading = false }) {
  if (loading) {
    return (
      <div className="glass-card p-6">
        <Skeleton className="h-5 w-40 mb-4" />
        {[1, 2, 3].map(i => (<div key={i} className="space-y-1 mb-3"><Skeleton className="h-4 w-full" /><Skeleton className="h-2 w-full" /></div>))}
      </div>
    );
  }

  const maxTasks = Math.max(...departments.map(d => d.active_tasks || 0), 1);

  return (
    <div className="glass-card p-6" data-testid="department-breakdown-card">
      <h3 className="text-sm font-semibold text-[#1a1a2e] mb-4 flex items-center gap-2">
        <Building2 className="w-4 h-4 text-brutal-pink" /> Department Workload
      </h3>
      {departments.length === 0 ? (
        <p className="text-sm text-neutral-400 text-center py-4">No departments found</p>
      ) : (
        departments.map((dept, idx) => (
          <div key={idx} className="mb-3">
            <div className="flex items-center justify-between text-sm mb-1.5">
              <span className="font-medium text-[#1a1a2e] text-xs">{dept.name}</span>
              <div className="flex items-center gap-2">
                <span className="font-mono-data text-xs text-neutral-500">{dept.active_tasks} active</span>
                {dept.overdue > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-600">{dept.overdue} overdue</span>
                )}
              </div>
            </div>
            <div className="w-full bg-neutral-100 rounded-full h-1.5">
              <div className="h-1.5 rounded-full bg-gradient-to-r from-brutal-pink to-pink-400 transition-all" style={{ width: `${(dept.active_tasks / maxTasks) * 100}%` }} />
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// Top Performers Card
function TopPerformersCard({ performers, loading = false }) {
  if (loading) {
    return (
      <div className="glass-card p-6">
        <Skeleton className="h-5 w-32 mb-4" />
        {[1, 2, 3].map(i => (
          <div key={i} className="flex items-center gap-3 mb-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <div className="flex-1 space-y-1"><Skeleton className="h-4 w-24" /><Skeleton className="h-3 w-16" /></div>
            <Skeleton className="h-6 w-12" />
          </div>
        ))}
      </div>
    );
  }

  const rankColors = ['from-amber-400 to-orange-400', 'from-slate-300 to-slate-400', 'from-amber-600 to-amber-700', 'from-neutral-200 to-neutral-300', 'from-neutral-200 to-neutral-300'];

  return (
    <div className="glass-card p-6" data-testid="top-performers-card">
      <h3 className="text-sm font-semibold text-[#1a1a2e] mb-4 flex items-center gap-2">
        <Award className="w-4 h-4 text-amber-500" /> Top Performers
      </h3>
      {performers.length === 0 ? (
        <EmptyState icon={Award} title="No data for this period" size="small" testId="empty-top-performers" />
      ) : (
        performers.map((perf, idx) => (
          <div key={idx} className="flex items-center gap-3 mb-3">
            <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${rankColors[idx] || rankColors[4]} flex items-center justify-center text-xs font-bold text-white shadow-sm`}>
              {idx + 1}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#1a1a2e] truncate">{perf.name}</p>
              <p className="text-xs text-neutral-400">{perf.department}</p>
            </div>
            <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">{perf.completed_tasks}</span>
          </div>
        ))
      )}
    </div>
  );
}

// Status Breakdown Card
function StatusBreakdownCard({ statusBreakdown, loading = false }) {
  if (loading) {
    return (
      <div className="glass-card p-6">
        <Skeleton className="h-5 w-32 mb-4" />
        <div className="grid grid-cols-2 gap-3">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-16 rounded-xl" />)}
        </div>
      </div>
    );
  }

  const statusConfig = {
    new: { label: 'New', bg: 'bg-blue-50', text: 'text-blue-700', icon: FileText },
    in_progress: { label: 'In Progress', bg: 'bg-amber-50', text: 'text-amber-700', icon: Activity },
    review: { label: 'Review', bg: 'bg-purple-50', text: 'text-purple-700', icon: Clock },
    revision: { label: 'Revision', bg: 'bg-red-50', text: 'text-red-700', icon: RefreshCw },
    completed: { label: 'Completed', bg: 'bg-emerald-50', text: 'text-emerald-700', icon: CheckCircle }
  };

  return (
    <div className="glass-card p-6" data-testid="status-breakdown-card">
      <h3 className="text-sm font-semibold text-[#1a1a2e] mb-4 flex items-center gap-2">
        <BarChart3 className="w-4 h-4 text-blue-500" /> Tasks by Status
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {Object.entries(statusBreakdown).map(([key, count]) => {
          const config = statusConfig[key] || { label: key, bg: 'bg-neutral-50', text: 'text-neutral-700', icon: FileText };
          const StatusIcon = config.icon;
          return (
            <div key={key} className={`${config.bg} rounded-xl p-3 text-center`}>
              <StatusIcon className={`w-4 h-4 mx-auto mb-1 ${config.text} opacity-60`} />
              <p className={`text-lg font-bold ${config.text}`}>{count}</p>
              <p className={`text-xs ${config.text} opacity-70`}>{config.label}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Priority Breakdown Card
function PriorityBreakdownCard({ priorityBreakdown, loading = false }) {
  if (loading) {
    return (
      <div className="glass-card p-6">
        <Skeleton className="h-5 w-32 mb-4" />
        <div className="flex gap-2">{[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-12 flex-1 rounded-xl" />)}</div>
      </div>
    );
  }

  const priorityConfig = {
    critical: { label: 'Critical', gradient: 'metric-gradient-red' },
    high: { label: 'High', gradient: 'metric-gradient-orange' },
    medium: { label: 'Medium', gradient: 'metric-gradient-amber' },
    low: { label: 'Low', gradient: 'metric-gradient-slate' }
  };

  return (
    <div className="glass-card p-6" data-testid="priority-breakdown-card">
      <h3 className="text-sm font-semibold text-[#1a1a2e] mb-4 flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-brutal-pink" /> Active by Priority
      </h3>
      <div className="flex gap-2">
        {Object.entries(priorityBreakdown).map(([key, count]) => {
          const config = priorityConfig[key] || { label: key, gradient: 'metric-gradient-slate' };
          return (
            <div key={key} className={`flex-1 ${config.gradient} rounded-xl p-3 text-center`}>
              <p className="text-lg font-bold text-white">{count}</p>
              <p className="text-xs text-white/80">{config.label}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Period Selector
function PeriodSelector({ period, onPeriodChange, customStart, customEnd, onCustomStartChange, onCustomEndChange, onCustomApply }) {
  const [showCustom, setShowCustom] = useState(period === 'custom');

  const handlePeriodChange = (value) => {
    if (value === 'custom') {
      onPeriodChange('custom');
      setTimeout(() => setShowCustom(true), 100);
    } else {
      setShowCustom(false);
      onPeriodChange(value);
    }
  };

  const applyCustomRange = () => {
    if (customStart && customEnd) {
      if (onCustomApply) onCustomApply();
      setShowCustom(false);
    }
  };

  return (
    <div className="flex items-center gap-2" data-testid="period-selector">
      <Select value={period} onValueChange={handlePeriodChange}>
        <SelectTrigger className="w-[160px] bg-white/80 backdrop-blur-sm rounded-xl border-white/40">
          <Calendar className="w-4 h-4 mr-2 text-neutral-400" />
          <SelectValue placeholder="Select period" />
        </SelectTrigger>
        <SelectContent className="bg-white rounded-xl">
          {PERIOD_OPTIONS.map(opt => (
            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {period === 'custom' && (
        <Popover open={showCustom} onOpenChange={setShowCustom}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="text-xs rounded-xl" data-testid="custom-range-trigger">
              {customStart && customEnd ? `${customStart} to ${customEnd}` : 'Set Range'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-4 bg-white rounded-xl" align="start">
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-neutral-600">Start Date</label>
                <Input type="date" value={customStart} onChange={(e) => onCustomStartChange(e.target.value)}
                  className="mt-1 rounded-xl" data-testid="custom-start-date" />
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-600">End Date</label>
                <Input type="date" value={customEnd} onChange={(e) => onCustomEndChange(e.target.value)}
                  className="mt-1 rounded-xl" data-testid="custom-end-date" />
              </div>
              <Button onClick={applyCustomRange} size="sm" className="w-full rounded-xl"
                disabled={!customStart || !customEnd} data-testid="apply-custom-range-btn">
                Apply Range
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

// Main Admin Metrics Dashboard
export function AdminMetricsDashboard({ api, onTaskFilter }) {
  const [period, setPeriod] = useState('ytd');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [customApplyCount, setCustomApplyCount] = useState(0);

  const customStartRef = React.useRef(customStart);
  const customEndRef = React.useRef(customEnd);
  customStartRef.current = customStart;
  customEndRef.current = customEnd;

  const fetchMetrics = useCallback(async () => {
    try {
      if (period === 'custom' && (!customStartRef.current || !customEndRef.current)) return;
      setLoading(true);
      let url = `/api/v3/admin/metrics?period=${period}`;
      if (period === 'custom' && customStartRef.current && customEndRef.current) {
        url += `&start_date=${customStartRef.current}&end_date=${customEndRef.current}`;
      }
      const res = await api.get(url);
      setMetrics(res.data);
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [api, period, customApplyCount]);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  const handleRefresh = () => { setRefreshing(true); fetchMetrics(); };

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
  };

  const handleCustomApply = () => {
    setCustomApplyCount(c => c + 1);
  };

  const currentPeriodLabel = PERIOD_OPTIONS.find(p => p.value === period)?.label || 'Year to Date';

  return (
    <div className="space-y-6" data-testid="admin-metrics-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-[#1a1a2e]">Performance Metrics</h2>
          <p className="text-sm text-neutral-500 mt-0.5">
            {currentPeriodLabel} overview
          </p>
        </div>
        <div className="flex items-center gap-2">
          <PeriodSelector
            period={period} onPeriodChange={handlePeriodChange}
            customStart={customStart} customEnd={customEnd}
            onCustomStartChange={setCustomStart} onCustomEndChange={setCustomEnd}
            onCustomApply={handleCustomApply}
          />
          <Button variant="outline" size="icon" onClick={handleRefresh} disabled={refreshing}
            className="rounded-xl bg-white/80 backdrop-blur-sm border-white/40"
            data-testid="refresh-metrics-btn">
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Primary Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 stagger-in">
        <MetricCard title="Tasks Created" value={metrics?.task_metrics?.created || 0}
          subtitle={`In ${currentPeriodLabel.toLowerCase()}`} icon={FileText} color="blue" loading={loading}
          onClick={() => onTaskFilter?.('all')} />
        <MetricCard title="Tasks Completed" value={metrics?.task_metrics?.completed || 0}
          subtitle={`In ${currentPeriodLabel.toLowerCase()}`} icon={CheckCircle} color="emerald" loading={loading}
          onClick={() => onTaskFilter?.('completed')} />
        <MetricCard title="Active Tasks" value={metrics?.task_metrics?.active || 0}
          subtitle="Current snapshot" icon={Activity} color="amber" loading={loading}
          onClick={() => onTaskFilter?.('active')} />
        <MetricCard title="Overdue" value={metrics?.task_metrics?.overdue || 0}
          subtitle="Past due date" icon={AlertTriangle} color="red" loading={loading}
          onClick={() => onTaskFilter?.('overdue')} />
        <MetricCard title="Active Employees" value={metrics?.team_metrics?.active_employees || 0}
          subtitle={metrics?.team_metrics?.new_hires > 0 ? `+${metrics.team_metrics.new_hires} new` : 'Team members'}
          icon={Users} color="purple" loading={loading} />
        <MetricCard title="Active Clients" value={metrics?.team_metrics?.active_clients || 0}
          subtitle={metrics?.team_metrics?.new_clients > 0 ? `+${metrics.team_metrics.new_clients} new` : 'Clients'}
          icon={Building2} color="cyan" loading={loading} />
      </div>

      {/* Rate Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 stagger-in">
        <ProgressMetricCard title="Completion Rate" value={metrics?.task_metrics?.completion_rate || 0}
          subtitle="Completed / Due in period" icon={Target} color="emerald" loading={loading} />
        <ProgressMetricCard title="On-Time Rate" value={metrics?.task_metrics?.on_time_rate || 0}
          subtitle="Completed before due" icon={Clock} color="blue" loading={loading} />
        <MetricCard title="Avg Completion" value={`${metrics?.task_metrics?.avg_completion_days || 0}d`}
          subtitle="Days to complete" icon={Zap} color="indigo" loading={loading} />
        <MetricCard title="Open Tickets" value={metrics?.help_desk?.open_tickets || 0}
          subtitle={`${metrics?.help_desk?.resolved_in_period || 0} resolved`}
          icon={HelpCircle} color="pink" loading={loading} />
      </div>

      {/* Applications */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 stagger-in">
        <MetricCard title="New Applications" value={metrics?.applications?.new_in_period || 0}
          subtitle={`In ${currentPeriodLabel.toLowerCase()}`} icon={UserPlus} color="teal" loading={loading} />
        <MetricCard title="Pending Applications" value={metrics?.applications?.pending || 0}
          subtitle="Awaiting review" icon={Briefcase} color="amber" loading={loading} />
        <MetricCard title="Tickets Created" value={metrics?.help_desk?.created_in_period || 0}
          subtitle={`In ${currentPeriodLabel.toLowerCase()}`} icon={FileText} color="slate" loading={loading} />
        <MetricCard title="Tickets Resolved" value={metrics?.help_desk?.resolved_in_period || 0}
          subtitle={`In ${currentPeriodLabel.toLowerCase()}`} icon={CheckCircle} color="emerald" loading={loading} />
      </div>

      {/* Status & Priority */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <StatusBreakdownCard statusBreakdown={metrics?.status_breakdown || {}} loading={loading} />
        <PriorityBreakdownCard priorityBreakdown={metrics?.priority_breakdown || {}} loading={loading} />
      </div>

      {/* Department & Performers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DepartmentBreakdownCard departments={metrics?.department_breakdown || []} loading={loading} />
        <TopPerformersCard performers={metrics?.top_performers || []} loading={loading} />
      </div>
    </div>
  );
}

// Employee Metrics Dashboard
export function EmployeeMetricsDashboard({ api, onTaskFilter }) {
  const [period, setPeriod] = useState('ytd');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [customApplyCount, setCustomApplyCount] = useState(0);

  const customStartRef = React.useRef(customStart);
  const customEndRef = React.useRef(customEnd);
  customStartRef.current = customStart;
  customEndRef.current = customEnd;

  const fetchMetrics = useCallback(async () => {
    try {
      if (period === 'custom' && (!customStartRef.current || !customEndRef.current)) return;
      setLoading(true);
      let url = `/api/v3/employee/metrics?period=${period}`;
      if (period === 'custom' && customStartRef.current && customEndRef.current) {
        url += `&start_date=${customStartRef.current}&end_date=${customEndRef.current}`;
      }
      const res = await api.get(url);
      setMetrics(res.data);
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [api, period, customApplyCount]);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  const handleRefresh = () => { setRefreshing(true); fetchMetrics(); };

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
  };

  const handleCustomApply = () => {
    setCustomApplyCount(c => c + 1);
  };

  const currentPeriodLabel = PERIOD_OPTIONS.find(p => p.value === period)?.label || 'Year to Date';

  return (
    <div className="space-y-6" data-testid="employee-metrics-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-[#1a1a2e]">My Performance</h2>
          <p className="text-sm text-neutral-500 mt-0.5">
            {currentPeriodLabel} overview {metrics?.department_context?.departments?.length ? `- ${metrics.department_context.departments.join(', ')}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <PeriodSelector period={period} onPeriodChange={handlePeriodChange}
            customStart={customStart} customEnd={customEnd}
            onCustomStartChange={setCustomStart} onCustomEndChange={setCustomEnd}
            onCustomApply={handleCustomApply} />
          <Button variant="outline" size="icon" onClick={handleRefresh} disabled={refreshing}
            className="rounded-xl bg-white/80 backdrop-blur-sm border-white/40"
            data-testid="refresh-metrics-btn">
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* My Workload */}
      <div>
        <h3 className="text-xs font-semibold text-neutral-500 tracking-wide mb-3">My Workload</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 stagger-in">
          <MetricCard title="Assigned to Me" value={metrics?.my_tasks?.active || 0}
            subtitle="Active tasks I own" icon={FileText} color="blue" loading={loading}
            onClick={() => onTaskFilter?.('my')} />
          <MetricCard title="Overdue" value={metrics?.my_tasks?.overdue || 0}
            subtitle="Past due date" icon={AlertTriangle} color="red" loading={loading}
            onClick={() => onTaskFilter?.('overdue')} />
          <MetricCard title="Due Today" value={metrics?.upcoming?.due_today || 0}
            subtitle="Needs attention now" icon={Clock} color="pink" loading={loading}
            onClick={() => onTaskFilter?.('due_today')} />
          <MetricCard title="Due This Week" value={metrics?.upcoming?.due_this_week || 0}
            subtitle="Plan ahead" icon={Calendar} color="indigo" loading={loading}
            onClick={() => onTaskFilter?.('due_week')} />
        </div>
      </div>

      {/* Created by Me */}
      <div>
        <h3 className="text-xs font-semibold text-neutral-500 tracking-wide mb-3">Created by Me</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 stagger-in">
          <MetricCard title="Active Created" value={metrics?.created_by_me?.total_active || 0}
            subtitle="Tasks I initiated" icon={FileText} color="violet" loading={loading}
            onClick={() => onTaskFilter?.('created')} />
          <MetricCard title="Awaiting Completion"
            value={(metrics?.created_by_me?.total_active || 0) - (metrics?.created_by_me?.needs_attention || 0)}
            subtitle="In progress by others" icon={Activity} color="amber" loading={loading}
            onClick={() => onTaskFilter?.('created')} />
          <MetricCard title="Needs Attention" value={metrics?.created_by_me?.needs_attention || 0}
            subtitle="In Review / Revision" icon={AlertTriangle} color="orange" loading={loading}
            onClick={() => onTaskFilter?.('created')} />
          <MetricCard title="Completed" value={metrics?.created_by_me?.completed_in_period || 0}
            subtitle={`In ${currentPeriodLabel.toLowerCase()}`} icon={CheckCircle} color="emerald" loading={loading} />
        </div>
      </div>

      {/* Output & Performance */}
      <div>
        <h3 className="text-xs font-semibold text-neutral-500 tracking-wide mb-3">Output & Performance</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 stagger-in">
          <MetricCard title="Assigned This Period" value={metrics?.my_tasks?.assigned_in_period || 0}
            subtitle={currentPeriodLabel} icon={FileText} color="cyan" loading={loading} />
          <MetricCard title="Completed This Period" value={metrics?.my_tasks?.completed_in_period || 0}
            subtitle={currentPeriodLabel} icon={CheckCircle} color="emerald" loading={loading}
            onClick={() => onTaskFilter?.('completed')} />
          <ProgressMetricCard title="Completion Rate" value={metrics?.my_tasks?.completion_rate || 0}
            subtitle="Completed / Due" icon={Target} color="emerald" loading={loading} />
          <ProgressMetricCard title="On-Time Rate" value={metrics?.my_tasks?.on_time_rate || 0}
            subtitle="Before deadline" icon={Clock} color="blue" loading={loading} />
        </div>
      </div>

      {/* Department Context */}
      <div>
        <h3 className="text-xs font-semibold text-neutral-500 tracking-wide mb-3">Department Context</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 stagger-in">
          <MetricCard title="Dept Active" value={metrics?.department_context?.department_active_tasks || 0}
            subtitle={metrics?.department_context?.departments?.join(', ') || ''}
            icon={Users} color="purple" loading={loading}
            onClick={() => onTaskFilter?.('department')} />
          <ProgressMetricCard title="My Contribution" value={metrics?.department_context?.my_contribution_percent || 0}
            subtitle={`${metrics?.department_context?.my_department_tasks || 0} of ${metrics?.department_context?.department_active_tasks || 0}`}
            icon={Users} color="amber" loading={loading} />
          <ProgressMetricCard title="Created Completion" value={metrics?.created_by_me?.completion_rate || 0}
            subtitle="Tasks I created done" icon={Target} color="violet" loading={loading} />
          <MetricCard title="Avg Days" value={`${metrics?.my_tasks?.avg_completion_days || 0}d`}
            subtitle="To complete a task" icon={Clock} color="slate" loading={loading} />
        </div>
      </div>

      {/* Status & Priority */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <StatusBreakdownCard statusBreakdown={metrics?.status_breakdown || {}} loading={loading} />
        <PriorityBreakdownCard priorityBreakdown={metrics?.priority_breakdown || {}} loading={loading} />
      </div>
    </div>
  );
}

export default AdminMetricsDashboard;
